/**
 * ИИ-нормоконтроль разделов (НК-3a1) — общий модуль для вкладки «Проверки» в
 * «Нормоконтроле» (views/normcontrol.js) и пункта «Нормоконтроль» в карточке ИЛ
 * (views/sheets.js). В этой сессии БЕЗ ИИ: загрузка раздела (мастер), список
 * проверок, карточка с файлами/скачиванием, ручные замечания, ответы исполнителя
 * и решения нормоконтролёра. Раннер, стадии A/B и паспорт проекта — НК-3b.
 *
 * Экспорт: createChecksTable(container, opts) — data-table проверок;
 * openUploadWizard(opts) — форма загрузки на правой панели; openCheckCard(jobId) —
 * карточка. Данные: /api/normcheck (backend/app/api/normcheck.py).
 */
import { api, API_BASE } from '../api.js';
import { canDo } from '../auth.js';
import { createDataTable } from '../components/data-table.js';
import { showNotification } from '../components/notification.js';
import { openRecordPanel, closeRecordPanel } from '../components/record-panel.js';
import { pickSheet } from '../components/ref-picker.js';

const esc = (s) => String(s ?? '')
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
const apiErrorText = (e) => e?.detail ?? e?.message ?? 'Ошибка';
const clip = (s) => `<span class="dt-clip" title="${esc(s ?? '')}">${esc(s || '—')}</span>`;

export const DOC_KINDS = ['pd', 'rd', 'survey', 'inspection'];
export const DOC_KIND_LABELS = {
  pd: 'Раздел ПД', rd: 'Раздел РД', survey: 'Отчёт об изысканиях', inspection: 'Отчёт об обследовании',
};
export const STATUSES = ['queued', 'analyzing', 'verifying', 'awaiting_answers', 'on_decision', 'accepted', 'rework', 'failed'];
export const STATUS_LABELS = {
  queued: 'В очереди', analyzing: 'Анализ ИИ', verifying: 'Проверка', awaiting_answers: 'Ждёт ответов',
  on_decision: 'На решении', accepted: 'Принято', rework: 'На доработку', failed: 'Ошибка',
};
const SEVERITIES = ['critical', 'major', 'minor'];
const SEVERITY_LABELS = { critical: 'Критично', major: 'Существенно', minor: 'Незначительно' };
const CATEGORIES = ['норма', 'состав_раздела', 'оформление', 'несоответствие', 'исходные_данные', 'расчёт'];
const CATEGORY_LABELS = {
  норма: 'Норма', состав_раздела: 'Состав раздела', оформление: 'Оформление',
  несоответствие: 'Несоответствие', исходные_данные: 'Исходные данные', расчёт: 'Расчёт',
};
const ANSWER_LABELS = { fixed: 'Исправлено', disagree: 'Не согласен' };
const DECISION_LABELS = { accepted: 'Принято', dismissed: 'Снято', rework: 'На доработку' };
// НК-3c: связь с прошлой версией
const VERSION_REL_LABELS = {
  repeat: 'повтор', not_fixed: 'не исправлено, хотя заявлено', resolved: 'исправлено',
};

const kindLabel = (k) => DOC_KIND_LABELS[k] ?? k;
const statusLabel = (s) => STATUS_LABELS[s] ?? s;
const fmtDate = (d) => (d ? String(d).slice(0, 10).split('-').reverse().join('.') : '');
const sheetTitle = (j) => j.sheet_ip_no || j.sheet_short_name || j.sheet_project_no || (j.project_sheet_id ? `ИЛ #${j.project_sheet_id}` : '—');

// ── Общий диалог (как в normcontrol.js / counterparties.js) ──────────────────

function openDialog({ title, bodyHtml, confirmLabel = 'Сохранить', wide = false, onMount, onConfirm }) {
  return new Promise((resolve) => {
    const dlg = document.createElement('dialog');
    dlg.className = 'app-modal cp-modal' + (wide ? ' cp-modal-wide' : '');
    dlg.innerHTML = `
      <form method="dialog" class="modal-form">
        <h3 class="modal-title">${esc(title)}</h3>
        <div class="cp-dialog-body nc-dialog-body">${bodyHtml}</div>
        <div class="modal-actions">
          <button type="button" class="modal-cancel">Закрыть</button>
          ${onConfirm ? `<button type="submit" class="modal-confirm">${esc(confirmLabel)}</button>` : ''}
        </div>
      </form>`;
    document.body.appendChild(dlg);
    const close = (v) => { try { dlg.close(); } catch { /* закрыт */ } dlg.remove(); resolve(v); };
    dlg.querySelector('.modal-cancel').addEventListener('click', () => close(null));
    dlg.addEventListener('cancel', (e) => { e.preventDefault(); close(null); });
    dlg.querySelector('form').addEventListener('submit', async (e) => {
      e.preventDefault();
      if (!onConfirm) { close(null); return; }
      const btn = dlg.querySelector('.modal-confirm');
      btn.disabled = true;
      const res = await onConfirm(dlg);
      btn.disabled = false;
      if (res !== false) close(res);
    });
    onMount?.(dlg, close);
    dlg.showModal();
  });
}

const field = (name, label, value, attrs = '') =>
  `<label class="modal-field"><span class="modal-field-label">${esc(label)}</span>
     <input name="${name}" value="${esc(value ?? '')}" ${attrs} /></label>`;

// ── Таблица проверок ──────────────────────────────────────────────────────────

const countsCell = (j) => {
  const crit = j.findings_critical ? ` <span class="nc-crit" title="Критичных">${j.findings_critical}!</span>` : '';
  return `${j.findings_total}/<b>${j.findings_open}</b>${crit}`;
};

function columns(embedded) {
  const cols = [
    { key: 'ip', label: 'ИЛ', width: 150, render: j => clip(sheetTitle(j)) },
    { key: 'section', label: 'Раздел', width: 180, render: j => clip([j.section_code, j.section_name].filter(Boolean).join(' · ')) },
    { key: 'doc_kind', label: 'Вид', width: 150, render: j => esc(kindLabel(j.doc_kind)) },
    { key: 'version', label: 'Версия', width: 70, align: 'center', sortKey: 'version', render: j => `v${j.version}` },
    { key: 'executor', label: 'Исполнитель', width: 160, render: j => clip(j.executor_name) },
    { key: 'status', label: 'Статус', width: 130, render: j => `<span class="nc-jstatus nc-jstatus-${esc(j.status)}">${esc(statusLabel(j.status))}</span>` },
    { key: 'findings', label: 'Замечаний', width: 110, align: 'center', render: countsCell },
    { key: 'created_at', label: 'Дата', width: 100, sortKey: 'created_at', render: j => esc(fmtDate(j.created_at)) },
  ];
  return embedded ? cols.filter(c => c.key !== 'ip') : cols;
}

/**
 * Таблица/доска проверок. opts: { sheetId, sheetLabel } — если задан sheetId
 * (embedded, карточка ИЛ), таблица фильтруется по этому ИЛ, колонка «ИЛ» скрыта,
 * мастер открывается с готовым ИЛ, переключателя вида НЕТ. В глобальной вкладке
 * «Проверки» (!embedded) сверху — переключатель Таблица/Kanban (Канбан-А).
 * Возвращает { reload } для перечитывания активного вида.
 */
export function createChecksTable(container, opts = {}) {
  const embedded = opts.sheetId != null;
  if (embedded) return renderChecksDataTable(container, opts);

  // Глобальная «Проверки»: переключатель Таблица/Kanban (образец — Аукционы).
  container.innerHTML = `
    <div class="nc-checks-bar">
      <div class="tenders-modes" role="tablist">
        ${CHECK_MODES.map(m => `<button type="button" role="tab" class="tenders-mode" data-ncheck-mode="${m.key}">${esc(m.label)}</button>`).join('')}
      </div>
    </div>
    <div class="nc-checks-body" data-nc-checks-body></div>`;
  const body = container.querySelector('[data-nc-checks-body]');
  let table = null;
  let board = null;
  let mode = 'table';

  const setMode = (m) => {
    if (!CHECK_MODES.some(x => x.key === m)) m = 'table';
    mode = m;
    saveCheckMode(m);
    container.querySelectorAll('[data-ncheck-mode]').forEach((b) => {
      const active = b.dataset.ncheckMode === m;
      b.classList.toggle('is-active', active);
      b.setAttribute('aria-selected', String(active));
    });
    body.className = `nc-checks-body nc-checks-body--${m}`;
    table = null;
    board = null;
    if (m === 'table') table = renderChecksDataTable(body, opts);
    else board = createChecksBoard(body);
  };

  container.querySelector('.tenders-modes').addEventListener('click', (e) => {
    const b = e.target.closest('[data-ncheck-mode]');
    if (b) { e.preventDefault(); setMode(b.dataset.ncheckMode); }
  });
  // Дефолт — «Таблица» (eager): e2e и прежний UX ждут .dt сразу, без клика.
  setMode(loadCheckMode());
  return { reload: () => { if (mode === 'table') table?.reload(); else board?.reload(); } };
}

/** Табличный вид проверок (data-table). Используется и embedded, и в режиме «Таблица». */
function renderChecksDataTable(host, opts) {
  const embedded = opts.sheetId != null;
  const el = document.createElement('div');
  host.innerHTML = '';
  host.appendChild(el);

  const table = createDataTable(el, {
    storageKey: embedded ? null : 'ccpm_dt_normcheck',
    columns: columns(embedded),
    filters: embedded ? [] : [
      { key: 'doc_kind', label: 'Вид', multi: true, options: DOC_KINDS.map(v => ({ value: v, label: kindLabel(v) })) },
      { key: 'status', label: 'Статус', multi: true, options: STATUSES.map(v => ({ value: v, label: statusLabel(v) })) },
    ],
    actions: canDo('normcheck.upload'),
    search: embedded ? null : { placeholder: 'Исполнитель' },
    pageSizes: [50, 100, 200],
    defaultSort: '-created_at',
    emptyHint: embedded
      ? 'Проверок по этому листу ещё нет. Нажмите «Загрузить на проверку».'
      : 'Проверок пока нет. Нажмите «Загрузить на проверку».',
    rowId: j => j.id,
    onRowClick: (j) => openCheckCard(j.id),
    fetch: async ({ limit, offset, sort, q, filters }) => {
      const params = { limit, offset, sort };
      if (embedded) params.sheet = opts.sheetId;
      if (q) params.executor = q;
      if (filters?.doc_kind?.length) params.doc_kind = filters.doc_kind.join(',');
      if (filters?.status?.length) params.status = filters.status.join(',');
      return api.normcheck.jobs(params);
    },
  });
  if (canDo('normcheck.upload')) {
    table.setActions('<button type="button" class="dt-btn cp-primary" data-ncheck-new>Загрузить на проверку</button>');
  }
  el.addEventListener('click', (e) => {
    if (e.target.closest('[data-ncheck-new]')) {
      e.preventDefault();
      // Форма загрузки на правой панели (НК-3a-5). В embedded ИЛ зафиксирован, в
      // глобальной «Проверке» — выбирается из справочника. После создания —
      // перечитать таблицу и открыть карточку проверки.
      openUploadWizard({
        sheetId: embedded ? opts.sheetId : null,
        sheetLabel: embedded ? opts.sheetLabel : null,
        onDone: (job) => { table.reload(); openCheckCard(job.id); },
      });
    }
  });
  return table;
}

// ── Доска «Проверки» (Канбан-А): карточка = раздел, требующий действий людей ───
// Образец — Аукционы (tenders.js): базовые классы .pipeline-* (main.css) и
// .tenders-modes. Статусы ИИ-прогона (queued/analyzing/verifying) — не карточки,
// а read-only индикатор «ИИ проверяет: N». Решение нормоконтролёра —
// перетаскиванием карточки из «На решении» (право normcheck.decide).

const CHECK_MODE_KEY = 'ccpm_normcheck_mode';
const CHECK_MODES = [
  { key: 'table', label: 'Таблица' },
  { key: 'kanban', label: 'Kanban' },
];
const loadCheckMode = () => { try { return localStorage.getItem(CHECK_MODE_KEY) || 'table'; } catch { return 'table'; } };
const saveCheckMode = (m) => { try { localStorage.setItem(CHECK_MODE_KEY, m); } catch { /* не критично */ } };

// ИИ-прогон — не карточка, а индикатор. Активные (на доске) грузим без принятых.
const BOARD_AI_STATUSES = new Set(['queued', 'analyzing', 'verifying']);
const BOARD_OPEN_STATUSES = 'queued,analyzing,verifying,awaiting_answers,on_decision,rework,failed';
const BOARD_OPEN_LIMIT = 500;
const BOARD_ACCEPTED_LIMIT = 100;

const CHECK_BOARD_COLUMNS = [
  { id: 'need_data', title: 'Недостаточно данных' },
  { id: 'on_decision', title: 'На решении' },
  { id: 'awaiting', title: 'Ждёт ответов' },
  { id: 'rework', title: 'На доработку' },
  { id: 'accepted', title: 'Принято' },
  { id: 'failed', title: 'Ошибка' },
];

// Столбец карточки = job.status + агрегат по замечаниям (первое совпадение). ИИ-прогон → null.
function checkColumnOf(j) {
  if (BOARD_AI_STATUSES.has(j.status)) return null;
  if (j.status === 'failed') return 'failed';
  if (j.status === 'accepted') return 'accepted';
  if (j.status === 'rework') return 'rework';
  if ((j.findings_need_data || 0) > 0) return 'need_data';
  if ((j.findings_answered || 0) > 0) return 'on_decision';
  if ((j.findings_open || 0) > 0) return 'awaiting';
  if ((j.findings_rework || 0) > 0) return 'rework';
  return 'accepted';   // всё решено, возвратов нет (или замечаний не было)
}

// Перетащить можно только из «На решении» в «Принято»/«На доработку».
const checkDropAllowed = (from, to) => from === 'on_decision' && (to === 'accepted' || to === 'rework');

// Счётчик карточки из существующих полей (см. расхождение №1 плана).
function cardCounts(j) {
  const total = j.findings_total || 0;
  if (!total) return 'нет замечаний';
  const open = j.findings_open || 0;
  const done = total - open;
  return `снято ${done} / осталось ${open} · ${Math.round((done / total) * 100)}%`;
}

function renderCheckCard(j) {
  const col = checkColumnOf(j);
  const draggable = col === 'on_decision' && canDo('normcheck.decide');
  const section = [j.section_code, j.section_name].filter(Boolean).join(' — ') || '—';
  return `
    <article class="pipeline-card nc-check-card" draggable="${draggable}" data-job-id="${j.id}" data-col="${esc(col)}">
      <div class="tenders-card-title">${esc(sheetTitle(j))} · ${esc(section)}</div>
      <div class="pipeline-card-meta"><span>${esc(kindLabel(j.doc_kind))}</span><span>v${esc(j.version)}</span></div>
      <div class="pipeline-card-tasks">${esc(cardCounts(j))}</div>
    </article>`;
}

// Подтверждение решения по всему разделу (перетаскивание). Без подтверждения ничего не шлём.
function openDecisionConfirm(accepted, openCount) {
  const n = openCount || 0;
  return openDialog({
    title: accepted ? 'Принять весь раздел?' : 'Вернуть раздел на доработку?',
    confirmLabel: accepted ? 'Принять раздел' : 'Вернуть на доработку',
    bodyHtml: `
      <p class="nc-muted">${accepted
        ? `Все открытые замечания раздела (${n}) будут отмечены принятыми — раздел принимается целиком как есть.`
        : `Все открытые замечания раздела (${n}) будут отмечены «на доработку», раздел вернётся исполнителю.`}</p>
      <label class="modal-field"><span class="modal-field-label">Причина / комментарий (необязательно)</span>
        <textarea name="reason" rows="3" class="modal-textarea"></textarea></label>`,
    onConfirm: (dlg) => ({ reason: dlg.querySelector('[name="reason"]').value.trim() || null }),
  });
}

/** Доска «Проверки» (замыкание на host). Возвращает { reload }. */
function createChecksBoard(host) {
  const state = {
    items: [], analyzing: 0,
    openTotal: 0, openShown: 0, acceptedTotal: 0, acceptedShown: 0, error: null,
  };

  function setError(msg) {
    state.error = msg;
    const el = host.querySelector('.nc-checks-board-error');
    if (!el) return;
    el.textContent = msg ?? '';
    el.hidden = !msg;
  }

  function render() {
    const groups = Object.fromEntries(CHECK_BOARD_COLUMNS.map(c => [c.id, []]));
    state.items.forEach((j) => { const col = checkColumnOf(j); if (col) groups[col].push(j); });
    const hints = [];
    if (state.openTotal > state.openShown) hints.push(`Показаны ${state.openShown} из ${state.openTotal} активных.`);
    if (state.acceptedTotal > state.acceptedShown) hints.push(`Показаны последние ${state.acceptedShown} принятых из ${state.acceptedTotal}.`);
    const ai = state.analyzing
      ? `<span class="nc-checks-ai" title="Задания, которые ИИ проверяет прямо сейчас">ИИ проверяет: ${state.analyzing}</span>` : '';
    host.innerHTML = `
      <div class="nc-checks-board-bar">
        ${ai}
        <span class="tenders-hint">Решение нормоконтролёра — перетащите карточку из «На решении» в «Принято» или «На доработку»${canDo('normcheck.decide') ? '' : ' (нужно право решения)'}. Отдельные замечания решаются в карточке.</span>
        ${hints.length ? `<span class="tenders-hint">${hints.join(' ')}</span>` : ''}
      </div>
      <div class="nc-checks-board-error"${state.error ? '' : ' hidden'} role="alert">${esc(state.error ?? '')}</div>
      <div class="nc-checks-grid-host">
        <div class="pipeline-grid nc-checks-grid">
          ${CHECK_BOARD_COLUMNS.map(c => `
            <section class="pipeline-col" data-col="${c.id}">
              <header class="pipeline-col-header">
                <span class="pipeline-col-title">${esc(c.title)}</span>
                <span class="pipeline-col-count">${groups[c.id].length}</span>
              </header>
              <div class="pipeline-col-body" data-drop-target="${c.id}">
                ${groups[c.id].map(renderCheckCard).join('') || '<div class="pipeline-col-empty">пусто</div>'}
              </div>
            </section>`).join('')}
        </div>
      </div>`;
    bind();
  }

  function bind() {
    const grid = host.querySelector('.nc-checks-grid');
    if (!grid) return;
    let drag = null;
    grid.addEventListener('dragstart', (e) => {
      const card = e.target.closest('.pipeline-card');
      if (!card || card.getAttribute('draggable') !== 'true') return;
      drag = { id: Number(card.dataset.jobId), from: card.dataset.col };
      card.classList.add('dragging');
      e.dataTransfer.effectAllowed = 'move';
      grid.querySelectorAll('.pipeline-col-body').forEach((t) => {
        const to = t.dataset.dropTarget;
        if (to === drag.from) return;
        t.classList.add(checkDropAllowed(drag.from, to) ? 'drop-ok' : 'drop-forbidden');
      });
    });
    grid.addEventListener('dragend', () => {
      grid.querySelectorAll('.dragging').forEach(c => c.classList.remove('dragging'));
      grid.querySelectorAll('.pipeline-col-body').forEach(t => t.classList.remove('drop-ok', 'drop-forbidden', 'drop-hover'));
      drag = null;
    });
    grid.addEventListener('dragover', (e) => {
      const t = e.target.closest('.pipeline-col-body');
      if (!t || !drag) return;
      e.preventDefault();   // принимаем и запрещённые — объясним текстом, почему нельзя
      t.classList.add('drop-hover');
    });
    grid.addEventListener('dragleave', (e) => e.target.closest('.pipeline-col-body')?.classList.remove('drop-hover'));
    grid.addEventListener('drop', async (e) => {
      const t = e.target.closest('.pipeline-col-body');
      if (!t || !drag) return;
      e.preventDefault();
      const to = t.dataset.dropTarget;
      const { id, from } = drag;
      if (to === from) return;   // свой столбец — ничего
      await move(id, from, to);
    });
    grid.addEventListener('click', (e) => {
      const card = e.target.closest('.pipeline-card');
      if (card && !card.classList.contains('dragging')) openCheckCard(Number(card.dataset.jobId));
    });
  }

  async function move(id, from, to) {
    if (!checkDropAllowed(from, to)) {
      setError('Решение переносом доступно только для карточек из «На решении» — в «Принято» или «На доработку».');
      return;
    }
    if (!canDo('normcheck.decide')) {
      setError('Решение нормоконтролёра выносит владелец или администратор.');
      return;
    }
    const job = state.items.find(j => j.id === id);
    const accepted = to === 'accepted';
    const res = await openDecisionConfirm(accepted, job?.findings_open || 0);
    if (!res) return;   // отменили — ничего не шлём
    try {
      await api.normcheck.jobDecision(id, { decision: accepted ? 'accepted' : 'rework', decision_reason: res.reason });
      setError(null);
      showNotification(accepted ? 'Раздел принят' : 'Раздел возвращён на доработку', 'success');
    } catch (e) {
      if (e.message === '401') return;
      setError(apiErrorText(e));
    }
    await load();
  }

  async function load() {
    if (!state.items.length) host.innerHTML = '<div class="loading-spinner">Загрузка…</div>';
    try {
      // Два запроса (как в Аукционах), чтобы «Принято» не вытесняло активные карточки.
      const [open, accepted] = await Promise.all([
        api.normcheck.jobs({ status: BOARD_OPEN_STATUSES, limit: BOARD_OPEN_LIMIT, sort: '-created_at' }),
        api.normcheck.jobs({ status: 'accepted', limit: BOARD_ACCEPTED_LIMIT, sort: '-updated_at' }),
      ]);
      state.items = [...open.items, ...accepted.items];
      state.openTotal = open.total; state.openShown = open.items.length;
      state.acceptedTotal = accepted.total; state.acceptedShown = accepted.items.length;
      state.analyzing = open.items.filter(j => BOARD_AI_STATUSES.has(j.status)).length;
      render();
    } catch (e) {
      if (e.message === '401') return;
      host.innerHTML = `<div class="tenders-error">Ошибка загрузки: ${esc(apiErrorText(e))}</div>`;
    }
  }

  load();
  return { reload: load };
}

// ── Форма загрузки раздела на правой панели (НК-3a-5) ─────────────────────────

async function loadExecutors() {
  const users = [];
  const people = [];
  try { const u = await api.expertise.usersForLink(); users.push(...(u || [])); } catch { /* нет права — пропускаем */ }
  try { const p = await api.refs.persons({ active: true, limit: 300 }); people.push(...(p.items || [])); } catch { /* нет справочника */ }
  return { users, people };
}
const execText = (sel) => (sel.value ? sel.options[sel.selectedIndex].text : '');

/**
 * Загрузка раздела на нормоконтроль — форма создания на правой панели записи
 * (record-panel, стиль forms.css; как «новый ИЛ» в УИ-2d). opts:
 *   - sheetId / sheetLabel — зафиксировать ИЛ (карточка ИЛ или повторная загрузка);
 *     иначе ИЛ выбирается из справочника (ref-picker pickSheet, СПР-1b).
 *   - prevJob — «Загрузить исправленную версию»: раздел/вид предзаполнены, prev_job_id.
 *   - onDone(job) — после успешного создания (перечитать таблицу, открыть карточку).
 * Панель открыта readonly (свой футер «Отправить»/«Отмена» — в теле формы), поэтому
 * загрузка файлов и живые подсказки (версия, состав ИЛ) работают без перерисовки.
 */
export function openUploadWizard(opts = {}) {
  openRecordPanel(null, {
    title: opts.prevJob ? 'Загрузить исправленную версию' : 'Загрузить раздел на проверку',
    readonly: true,
    resizeKey: 'normcheck',
    resizeDefaultWidth: 640,
    render: (rec, ctx) => { renderUploadFormInto(ctx.host, opts); },
  });
}

async function renderUploadFormInto(host, opts) {
  const fixedSheet = opts.sheetId != null;
  const prev = opts.prevJob || null;
  host.innerHTML = '<div class="loading-spinner">Загрузка…</div>';
  const executors = await loadExecutors();

  const execOptions = [];
  if (executors.users.length) {
    execOptions.push(`<optgroup label="Сотрудники">${executors.users.map(u => `<option value="user:${u.id}">${esc(u.full_name || ('#' + u.id))}</option>`).join('')}</optgroup>`);
  }
  if (executors.people.length) {
    execOptions.push(`<optgroup label="Люди">${executors.people.map(p => `<option value="person:${p.id}">${esc(p.fio)}</option>`).join('')}</optgroup>`);
  }
  const kindOptions = DOC_KINDS.map(k => `<option value="${k}"${prev?.doc_kind === k ? ' selected' : ''}>${esc(kindLabel(k))}</option>`).join('');

  // Поле ИЛ: зафиксирован (только вывод) или выбор из справочника (ref-picker).
  const sheetField = fixedSheet
    ? `<div class="modal-field modal-field-wide"><span class="modal-field-label">Исполнительный лист</span>
         <div class="nc-fixed-sheet">${esc(opts.sheetLabel || `ИЛ #${opts.sheetId}`)}</div></div>`
    : `<div class="modal-field modal-field-wide"><span class="modal-field-label">Исполнительный лист</span>
         <input type="hidden" name="sheet_id" value="" />
         <span class="ref-link">
           <span class="ref-link-status" data-nc-sheet-name>не выбран</span>
           <button type="button" class="dt-link" data-nc-pick-sheet>из справочника…</button>
         </span></div>`;

  host.innerHTML = `
    <form class="record-panel-form modal-grid" data-nc-upload novalidate>
      ${sheetField}
      <p class="modal-field-wide modal-field-hint" data-nc-ird></p>
      <label class="modal-field modal-field-wide"><span class="modal-field-label">Раздел</span>
        <select name="section_template_id" class="modal-select" required>
          <option value="">— выберите раздел —</option>
        </select></label>
      <p class="modal-field-wide modal-field-hint" data-nc-section-pick></p>
      <p class="modal-field-wide modal-field-hint" data-nc-dep-warn></p>
      <label class="modal-field"><span class="modal-field-label">Вид документа</span>
        <select name="doc_kind" class="modal-select">${kindOptions}</select></label>
      <label class="modal-field"><span class="modal-field-label">Исполнитель</span>
        <select name="executor" class="modal-select">
          <option value="">— ввести вручную —</option>
          ${execOptions.join('')}
        </select></label>
      ${field('executor_name', 'ФИО исполнителя (если нет в списке)', '', 'placeholder="Фамилия И.О."')}
      <label class="modal-field modal-field-wide"><span class="modal-field-label">Раздел на проверку (PDF/DOCX, можно несколько)</span>
        <input type="file" name="main" accept=".pdf,.docx" multiple required /></label>
      <label class="modal-field modal-field-wide"><span class="modal-field-label">Исходные данные — ТЗ, ИРД (необязательно)</span>
        <input type="file" name="source_data" accept=".pdf,.docx" multiple /></label>
      <p class="modal-field-wide modal-field-hint" data-nc-version></p>
      <div class="modal-actions modal-field-wide">
        <button type="button" class="modal-cancel" data-nc-cancel>Отмена</button>
        <button type="submit" class="modal-confirm" data-nc-submit>Отправить</button>
      </div>
    </form>`;

  const form = host.querySelector('[data-nc-upload]');
  const g = (n) => form.elements[n];
  const sheetId = () => (fixedSheet ? opts.sheetId : Number(g('sheet_id').value || 0) || null);

  // НК-3a-6: раздел — из справочника. Фильтр по стадии от вида документа; inspection —
  // без фильтра (чистого соответствия стадии нет). {stage: undefined} даёт пустой список,
  // поэтому для inspection передаём {} (без ключа stage вовсе).
  const stageFor = (dk) => ({ pd: { stage: 'p' }, rd: { stage: 'r' }, survey: { stage: 'survey' } }[dk] || {});
  const selectedOption = () => { const sel = g('section_template_id'); return sel.value ? sel.selectedOptions[0] : null; };
  const updateSectionPick = () => {
    const out = host.querySelector('[data-nc-section-pick]');
    const o = selectedOption();
    out.textContent = o ? `Выбран раздел: ${[o.dataset.code, o.dataset.name].filter(Boolean).join(' — ')}` : '';
  };
  const clearDepWarn = () => { host.querySelector('[data-nc-dep-warn]').textContent = ''; };

  const refreshSections = async () => {
    const sel = g('section_template_id');
    const prevVal = sel.value;
    let catalog = [];
    try { catalog = (await api.listSections(stageFor(g('doc_kind').value))) || []; } catch { catalog = []; }
    // Коды состава этого ИЛ — для группы «сверху, с пометкой». Эндпоинт состава требует
    // expertise.read: нет права → просто без группы «Состав» (список всё равно придёт).
    const compCodes = new Set();
    const sid = sheetId();
    if (sid) {
      try {
        const comp = await api.expertise.sheetComposition(sid);
        for (const c of (comp || [])) { if (c.section_code) compCodes.add(String(c.section_code)); }
      } catch { /* нет права/состава — пропускаем */ }
    }
    const opt = (s) => `<option value="${s.id}" data-code="${esc(s.code ?? '')}" data-name="${esc(s.name ?? '')}">${esc([s.code, s.name].filter(Boolean).join(' — '))}</option>`;
    const inComp = catalog.filter(s => compCodes.has(String(s.code)));
    const rest = catalog.filter(s => !compCodes.has(String(s.code)));
    let html = '<option value="">— выберите раздел —</option>';
    if (inComp.length) html += `<optgroup label="Состав этого ИЛ">${inComp.map(opt).join('')}</optgroup>`;
    html += `<optgroup label="Все разделы">${rest.map(opt).join('')}</optgroup>`;
    sel.innerHTML = html;
    // Сохранить выбор, если раздел остался в новом списке; иначе сбросить и очистить предупреждение.
    if (prevVal && sel.querySelector(`option[value="${prevVal}"]`)) {
      sel.value = prevVal;
    } else {
      sel.value = '';
      clearDepWarn();
    }
    updateSectionPick();
  };

  const refreshDepWarn = async () => {
    const out = host.querySelector('[data-nc-dep-warn]');
    out.textContent = '';
    const sid = sheetId();
    const o = selectedOption();
    if (!sid || !o) return;
    try {
      const res = await api.normcheck.sectionDepsCheck({ project_sheet_id: sid, section_template_id: o.value });
      const pending = (res.predecessors || []).filter(p => !p.accepted);
      if (pending.length) {
        const name = o.dataset.name || o.dataset.code || 'раздел';
        const list = pending
          .map(p => `${[p.code, p.name].filter(Boolean).join(' ')} (${p.last_status ? statusLabel(p.last_status) : 'не загружался'})`)
          .join('; ');
        out.textContent = `⚠ Раздел ${name} обычно проверяют после: ${list} — на этом ИЛ ещё не принят(ы). Отправить можно.`;
      }
    } catch { /* предупреждение необязательно */ }
  };
  const refreshVersion = async () => {
    const out = host.querySelector('[data-nc-version]');
    const sid = sheetId();
    if (!sid) { out.textContent = ''; return; }
    try {
      const params = { sheet: sid, doc_kind: g('doc_kind').value, limit: 1, sort: '-version' };
      const code = (selectedOption()?.dataset.code || '').trim();
      if (code) params.section_code = code;
      const r = await api.normcheck.jobs(params);
      const next = (r.items[0]?.version || 0) + 1;
      out.textContent = next > 1 ? `Будет версия ${next} (повторная загрузка этого раздела).` : 'Будет версия 1.';
    } catch { out.textContent = ''; }
  };
  const refreshIrd = (sid, name) => {
    const out = host.querySelector('[data-nc-ird]');
    out.textContent = sid
      ? `✓ ИЛ выбран${name ? `: ${name}` : ''}. Исходные данные листа (состав проекта, паспорт) доступны проверке; при необходимости приложите ТЗ/ИРД ниже.`
      : '';
  };

  if (!fixedSheet) {
    const setSheet = async (id, name) => {
      g('sheet_id').value = id || '';
      const disp = host.querySelector('[data-nc-sheet-name]');
      disp.textContent = id ? (name || `ИЛ #${id}`) : 'не выбран';
      disp.classList.toggle('is-linked', !!id);
      refreshIrd(id, name);
      await refreshSections();
      await refreshVersion();
      await refreshDepWarn();
    };
    host.querySelector('[data-nc-pick-sheet]').addEventListener('click', async () => {
      const res = await pickSheet({ currentId: sheetId() });
      if (res?.action === 'select') await setSheet(res.id, res.name);
      else if (res?.action === 'unlink') await setSheet(null, null);
    });
  } else {
    refreshIrd(opts.sheetId, opts.sheetLabel);
  }

  g('executor').addEventListener('change', () => {
    const sel = g('executor');
    g('executor_name').value = sel.value ? '' : g('executor_name').value;
    g('executor_name').placeholder = sel.value ? execText(sel) : 'Фамилия И.О.';
  });
  // Выбор раздела: подсказка + версия + предупреждение о предшественниках. Смена вида
  // документа перечитывает список по стадии (refreshSections сам сохранит/сбросит выбор).
  g('section_template_id').addEventListener('change', () => { updateSectionPick(); refreshVersion(); refreshDepWarn(); });
  g('doc_kind').addEventListener('change', async () => { await refreshSections(); await refreshVersion(); await refreshDepWarn(); });
  host.querySelector('[data-nc-cancel]').addEventListener('click', () => closeRecordPanel());
  form.addEventListener('submit', (e) => { e.preventDefault(); submitUpload(host, opts, fixedSheet, prev); });

  await refreshSections();
  // Повторная загрузка: предвыбрать раздел прошлой проверки (по id из справочника, иначе
  // по шифру для старых проверок без section_template_id; не нашли — выбирает пользователь).
  if (prev) {
    const sel = g('section_template_id');
    if (prev.section_template_id && sel.querySelector(`option[value="${prev.section_template_id}"]`)) {
      sel.value = String(prev.section_template_id);
    } else if (prev.section_code) {
      const match = [...sel.options].find(o => o.dataset.code === prev.section_code);
      if (match) sel.value = match.value;
    }
    updateSectionPick();
  }
  await refreshVersion();
  await refreshDepWarn();
}

async function submitUpload(host, opts, fixedSheet, prev) {
  const form = host.querySelector('[data-nc-upload]');
  const g = (n) => form.elements[n];
  const sid = fixedSheet ? opts.sheetId : (Number(g('sheet_id').value || 0) || 0);
  if (!sid) { showNotification('Выберите исполнительный лист', 'error'); return; }
  const main = [...(g('main').files || [])];
  if (!main.length) { showNotification('Добавьте файл раздела', 'error'); return; }
  const source = [...(g('source_data').files || [])];
  const sel = g('section_template_id');
  if (!sel.value) { showNotification('Выберите раздел', 'error'); return; }
  const o = sel.selectedOptions[0];
  const body = {
    project_sheet_id: sid,
    doc_kind: g('doc_kind').value,
    // Раздел из справочника: id — единый источник; шифр/наименование из data-* для превью
    // версии (бэкенд всё равно берёт их из section_templates по id).
    section_template_id: sel.value,
    section_code: o?.dataset.code || '',
    section_name: o?.dataset.name || '',
  };
  if (prev?.id) body.prev_job_id = prev.id;
  const exec = g('executor').value;
  if (exec.startsWith('user:')) { body.executor_user_id = exec.slice(5); body.executor_name = execText(g('executor')); }
  else if (exec.startsWith('person:')) { body.executor_person_id = exec.slice(7); body.executor_name = execText(g('executor')); }
  else { body.executor_name = g('executor_name').value.trim(); }

  const btn = form.querySelector('[data-nc-submit]');
  btn.disabled = true;
  try {
    const job = await api.normcheck.createJob(body, main, source);
    showNotification(`Раздел загружен на проверку (версия ${job.version})`, 'success');
    opts.onDone?.(job);
  } catch (e) {
    btn.disabled = false;
    if (e.message !== '401') showNotification(apiErrorText(e), 'error');
  }
}

// ── Карточка проверки ─────────────────────────────────────────────────────────

const PAGES_STATUS_LABELS = {
  queued: 'разметка в очереди', marking: 'идёт разметка…', done: 'разметка готова', failed: 'ошибка разметки',
};

const CARD_TABS = [
  { key: 'findings', label: 'Замечания' },
  { key: 'pages', label: 'Страницы' },
  { key: 'passport', label: 'Паспорт проекта' },
];

/**
 * УИ-2e — карточка проверки открывается в правой панели записи (record-panel),
 * а не в центральной модалке. Панель — синглтон (одна открыта за раз): открытие
 * карточки проверки ЗАМЕНЯЕТ содержимое панели (напр. карточку ИЛ), как и в ИЛ
 * (УИ-2d). Свой ключ ширины 'normcheck'. Тело карточки (шапка/Файлы/Ход/вкладки)
 * рисует render-body, под-формы (ответ/решение/замечание/превью/мастер) остаются
 * модалками поверх. Футер панели — «Закрыть» (readonly:true), собственные действия
 * карточки — внутри тела и скрыты без прав normcheck.upload/decide.
 */
export async function openCheckCard(jobId) {
  let job;
  try { job = await api.normcheck.job(jobId); }
  catch (e) { showNotification(apiErrorText(e), 'error'); return; }

  openRecordPanel(job, {
    title: checkCardTitle(job),
    resizeKey: 'normcheck',
    resizeDefaultWidth: 760,
    readonly: true,   // футер панели = «Закрыть»; свои кнопки действий — внутри тела
    render: (rec, ctx) => {
      ctx.host.classList.add('nc-check-pane');
      ctx.host.innerHTML = checkCardBodyHtml(rec);
      const ctl = new CheckCardController(ctx);
      ctx.onTeardown(() => ctl.destroy());
      ctl.start();
    },
  });
}

const checkCardTitle = (job) =>
  `Проверка: ${sheetTitle(job)} · ${[job.section_code, kindLabel(job.doc_kind)].filter(Boolean).join(' · ')} · v${job.version}`;

function checkCardBodyHtml(job) {
  return `
    <div class="cp-card nc-check-card">
      <dl class="cp-reqs">
        <dt>Исполнительный лист</dt>
        <dd><a class="dt-link" href="/projects/sheets?id=${job.project_sheet_id}" data-route="/projects/sheets?id=${job.project_sheet_id}">${esc(sheetTitle(job))}</a></dd>
        <dt>Раздел</dt><dd>${esc([job.section_code, job.section_name].filter(Boolean).join(' · ') || '—')}</dd>
        <dt>Вид документа</dt><dd>${esc(kindLabel(job.doc_kind))}</dd>
        <dt>Версия</dt><dd>v${job.version}${job.prev_job_id ? ` (исправленная, была #${job.prev_job_id})` : ''}</dd>
        <dt>Исполнитель</dt><dd>${esc(job.executor_name || '—')}</dd>
        <dt>Статус</dt><dd><span class="nc-jstatus nc-jstatus-${esc(job.status)}">${esc(statusLabel(job.status))}</span></dd>
      </dl>
      <div class="cp-card-section">
        <div class="nc-section-head"><h4>Файлы</h4></div>
        <div class="nc-files">${filesHtml(job.files)}</div>
      </div>
      <div class="cp-card-section nc-progress">
        <div class="nc-section-head"><h4>Ход ИИ-проверки</h4>
          <div class="nc-find-actions" data-nc-analyze-actions></div>
        </div>
        <div data-nc-progress><div class="loading-spinner">Загрузка…</div></div>
        <div data-nc-vcompare></div>
      </div>
      <nav class="nc-card-tabs" data-nc-ctabs>
        ${CARD_TABS.map((t, i) => `<button type="button" class="nc-card-tab${i === 0 ? ' is-active' : ''}" data-nc-ctab="${t.key}">${esc(t.label)}</button>`).join('')}
      </nav>
      <div class="nc-card-panel" data-nc-panel><div class="loading-spinner">Загрузка…</div></div>
    </div>`;
}

function filesHtml(files) {
  if (!files?.length) return '<p class="nc-muted">Файлов нет.</p>';
  const byRole = { main: [], source_data: [] };
  files.forEach(f => (byRole[f.role] || (byRole[f.role] = [])).push(f));
  const row = (f) => `<li>
      <a class="dt-link" href="${API_BASE}/api/normcheck/files/${f.id}" target="_blank" rel="noopener">${esc(f.filename)}</a>
      <span class="nc-muted">${f.pages ? `${f.pages} с., ` : ''}${Math.max(1, Math.round((f.file_size || 0) / 1024))} КБ</span>
    </li>`;
  const block = (label, arr) => (arr.length
    ? `<div class="nc-file-group"><div class="nc-file-group-t">${label}</div><ul class="nc-file-list">${arr.map(row).join('')}</ul></div>` : '');
  return block('Раздел', byRole.main) + block('Исходные данные', byRole.source_data);
}

const FRAG_STAGE = {
  queued: 'в очереди', running: 'идёт ИИ-анализ', done: 'готов', failed: 'ошибка',
};

class CheckCardController {
  // ctx — контекст render-body панели записи: { host, record, onTeardown, rerender, setRecord }.
  constructor(ctx) {
    this.ctx = ctx;
    this.root = ctx.host;          // тело карточки внутри .rp-pane (бывш. this.dlg)
    this.job = ctx.record;
    this.alive = true;             // teardown панели гасит висящие async-дорисовки
    this.findings = [];
    this.tab = 'findings';
    this.showHidden = false;
    this.selected = new Set();
    this.root.addEventListener('click', (e) => this.onClick(e));
    this.root.addEventListener('change', (e) => this.onChange(e));
  }

  // Панель закрылась/сменила запись — прекращаем дорисовки (опроса-таймера в карточке
  // нет, ход ИИ обновляется кнопкой «Обновить»; флаг страхует от записи в снятый DOM).
  destroy() { this.alive = false; }

  async start() {
    this.root.querySelector('[data-nc-ctabs]').addEventListener('click', (e) => {
      const btn = e.target.closest('[data-nc-ctab]');
      if (btn) { e.preventDefault(); this.showTab(btn.dataset.ncCtab); }
    });
    await this.renderProgress();
    if (!this.alive) return;
    await this.showTab('findings');
  }

  async renderProgress() {
    const host = this.root.querySelector('[data-nc-progress]');
    const actions = this.root.querySelector('[data-nc-analyze-actions]');
    if (!host) return;
    let fr;
    try {
      const [job, fragments] = await Promise.all([
        api.normcheck.job(this.job.id), api.normcheck.fragments(this.job.id),
      ]);
      if (!this.alive) return;       // панель закрылась/сменила запись за время запроса
      this.job = job;  // свежий pages_status/status
      fr = fragments;
    } catch (e) { if (this.alive) host.innerHTML = `<p class="cp-error">${esc(apiErrorText(e))}</p>`; return; }
    this.job.status = fr.status;
    const pagesReady = this.job.pages_status === 'done';
    const analyzing = fr.status === 'analyzing' || fr.running > 0 || fr.queued > 0;
    if (!fr.total) {
      host.innerHTML = pagesReady
        ? '<p class="ti-hint">Раздел размечен. Нажмите «Начать проверку» — ИИ найдёт замечания по нормам.</p>'
        : `<p class="ti-hint">${esc(PAGES_STATUS_LABELS[this.job.pages_status] || '')}. Проверку можно начать после разметки страниц.</p>`;
    } else {
      const done = fr.done + fr.failed;
      host.innerHTML = `
        <p class="ti-hint">Фрагментов ${done}/${fr.total} · статус: <b>${esc(statusLabel(fr.status))}</b>${fr.failed ? ` · <span class="nc-crit">ошибок: ${fr.failed}</span>` : ''}</p>
        <div class="nc-frag-list">${fr.items.map(f => `
          <span class="nc-frag nc-frag-${esc(f.status)}" title="${esc(f.kind === 'graphic' ? 'графика' : 'текст')}, стр. ${f.page_from}–${f.page_to}">
            #${f.part_no} ${esc(FRAG_STAGE[f.status] || f.status)}${f.error ? ` — ${esc(f.error.slice(0, 80))}` : ''}</span>`).join('')}</div>`;
    }
    if (actions) {
      const canRun = canDo('normcheck.upload') && pagesReady && !analyzing;
      actions.innerHTML = [
        '<button type="button" class="dt-btn nc-mini" data-nc-refresh-progress>Обновить</button>',
        canRun ? `<button type="button" class="dt-btn cp-primary" data-nc-analyze>${fr.total ? 'Проверить заново' : 'Начать проверку'}</button>` : '',
      ].join('');
    }
    await this.renderVersionCompare();
  }

  // НК-3c: блок «Сравнение с версией N-1» (исправлено / повторов / новых).
  async renderVersionCompare() {
    const host = this.root.querySelector('[data-nc-vcompare]');
    if (!host) return;
    if (!this.job.prev_job_id) { host.innerHTML = ''; return; }
    let vc;
    try { vc = await api.normcheck.versionCompare(this.job.id); }
    catch { host.innerHTML = ''; return; }
    if (!this.alive) return;
    const item = (f) => `<li class="nc-vc-item">
      <span class="nc-sev nc-sev-${esc(f.severity)}">${esc(SEVERITY_LABELS[f.severity] || f.severity)}</span>
      ${f.version_rel ? `<span class="nc-tag ${f.version_rel === 'not_fixed' ? 'nc-tag-notfixed' : 'nc-tag-repeat'}">${esc(VERSION_REL_LABELS[f.version_rel])}</span>` : ''}
      ${f.req_code ? `<span class="nc-muted">${esc(f.req_code)}</span> ` : ''}${esc(f.issue)}</li>`;
    host.innerHTML = `
      <div class="nc-vcompare">
        <b>Сравнение с версией ${vc.prev_version ? 'v' + vc.prev_version : 'N-1'}:</b>
        исправлено <b>${vc.fixed}</b> · повторов <b>${vc.repeat}</b>${vc.not_fixed ? ` · <span class="nc-crit">не исправлено, хотя заявлено: ${vc.not_fixed}</span>` : ''} · новых <b>${vc.new}</b>
        ${(vc.repeat_items.length || vc.fixed_items.length) ? `
          <details class="nc-vc-details"><summary>показать</summary>
            ${vc.repeat_items.length ? `<div class="nc-vc-group"><div class="nc-muted">Повторяющиеся в новой версии:</div><ul>${vc.repeat_items.map(item).join('')}</ul></div>` : ''}
            ${vc.fixed_items.length ? `<div class="nc-vc-group"><div class="nc-muted">Исправлено (нет в новой версии):</div><ul>${vc.fixed_items.map(item).join('')}</ul></div>` : ''}
          </details>` : ''}
      </div>`;
  }

  async showTab(tab) {
    this.tab = tab;
    this.root.querySelectorAll('[data-nc-ctab]').forEach(b =>
      b.classList.toggle('is-active', b.dataset.ncCtab === tab));
    const panel = this.root.querySelector('[data-nc-panel]');
    panel.innerHTML = '<div class="loading-spinner">Загрузка…</div>';
    if (tab === 'findings') await this.loadFindings(panel);
    else if (tab === 'pages') await renderPagesPanel(panel, this.job);
    else if (tab === 'passport') await renderPassportPanel(panel, { sheetId: this.job.project_sheet_id });
  }

  async loadFindings(panel) {
    const hiddenCount = this.findings.filter(f => f.hidden_by_b).length;
    panel.innerHTML = `
      <div class="nc-section-head nc-findings-head">
        <label class="nc-check-inline"><input type="checkbox" data-nc-show-hidden${this.showHidden ? ' checked' : ''} /> показать снятые верификатором</label>
        <div class="nc-find-actions">
          ${canDo('normcheck.decide') ? '<button type="button" class="dt-btn nc-mini" data-nc-bulk-dec="accepted">Принять выбранные</button><button type="button" class="dt-btn nc-mini" data-nc-bulk-dec="dismissed">Снять выбранные</button>' : ''}
          ${canDo('normcheck.upload') ? '<button type="button" class="dt-btn" data-nc-rework>Загрузить исправленную версию</button>' : ''}
          ${canDo('normcheck.decide') ? '<button type="button" class="dt-btn cp-primary" data-nc-add-finding>Добавить замечание вручную</button>' : ''}
        </div>
      </div>
      <div class="nc-findings" data-nc-findings><div class="loading-spinner">Загрузка…</div></div>`;
    const host = panel.querySelector('[data-nc-findings]');
    let res;
    try { res = await api.normcheck.findings(this.job.id); }
    catch (e) { if (this.alive) host.innerHTML = `<p class="cp-error">Ошибка: ${esc(apiErrorText(e))}</p>`; return; }
    if (!this.alive) return;
    this.findings = res.items;
    const visible = res.items.filter(f => this.showHidden || !f.hidden_by_b);
    if (!visible.length) {
      host.innerHTML = `<p class="nc-muted">Замечаний нет. Их найдёт ИИ («Начать проверку») или добавит нормоконтролёр вручную.${hiddenCount && !this.showHidden ? ` Снятых верификатором: ${hiddenCount}.` : ''}</p>`;
      return;
    }
    host.innerHTML = `<table class="nc-find-table"><thead><tr>
        ${canDo('normcheck.decide') ? '<th class="nc-find-sel"></th>' : ''}
        <th>Замечание</th><th>Норма</th><th>Ответ исполнителя</th><th>Решение</th>
      </tr></thead><tbody>${visible.map(f => this.findingRow(f)).join('')}</tbody></table>`;
  }

  // Метки: ИИ / сверка паспорта / не подтверждено / Opus / версии (повтор…).
  findingMarks(f) {
    const marks = [];
    if (f.origin === 'reconcile') marks.push('<span class="nc-tag nc-tag-disc" title="Сверка паспорта проекта (НК-3c)">сверка паспорта</span>');
    else if (f.model) marks.push('<span class="nc-tag nc-tag-ai" title="Найдено ИИ">ИИ</span>');
    if (f.resolved) marks.push('<span class="nc-tag nc-tag-resolved" title="Расхождение устранено">расхождение устранено</span>');
    if (f.verified_quote === false) marks.push('<span class="nc-tag nc-tag-unverified" title="Цитата не подтверждена программно">не подтверждено</span>');
    if (f.stage_b_verdict === 'confirm') marks.push('<span class="nc-tag nc-tag-opus" title="Проверено верификатором Opus">проверено Opus</span>');
    if (f.stage_b_verdict === 'adjust') marks.push('<span class="nc-tag nc-tag-opus" title="Уточнено верификатором Opus">уточнено Opus</span>');
    if (f.hidden_by_b) marks.push('<span class="nc-tag nc-tag-rejected" title="Снято верификатором Opus (reject)">снято верификатором</span>');
    if (f.version_rel) {
      const cls = f.version_rel === 'not_fixed' ? 'nc-tag-notfixed' : 'nc-tag-repeat';
      marks.push(`<span class="nc-tag ${cls}" title="Сравнение с прошлой версией">${esc(VERSION_REL_LABELS[f.version_rel] || f.version_rel)}</span>`);
    }
    return marks.length ? `<div class="nc-tags">${marks.join(' ')}</div>` : '';
  }

  findingRow(f) {
    const sev = `<span class="nc-sev nc-sev-${esc(f.severity)}">${esc(SEVERITY_LABELS[f.severity] || f.severity)}</span>`;
    const cat = f.category ? ` <span class="nc-muted">${esc(CATEGORY_LABELS[f.category] || f.category)}</span>` : '';
    const normLabel = [f.req_code, f.req_clause].filter(Boolean).join(' ') || (f.norm_status === 'нет_в_базе' ? 'нет в базе' : '—');
    const norm = f.norm_clause_id
      ? `<a class="dt-link" href="/projects/norm-control" data-route="/projects/norm-control" title="Открыть в «Нормативах»">${esc(normLabel)}</a>`
      : esc(normLabel);
    const normQuote = f.req_quote ? `<div class="nc-muted nc-quote">«${esc(f.req_quote)}»</div>` : '';
    const place = f.page_id
      ? `<a href="#" class="dt-link nc-place" data-nc-find-page="${f.page_id}">стр. ${esc(f.location_page || '')}${f.location_sheet ? ' · ' + esc(f.location_sheet) : ''}</a>`
      : (f.location_page ? `<span class="nc-muted">стр. ${esc(f.location_page)}</span>` : '');
    const ans = f.executor_answer
      ? `<span class="nc-ans nc-ans-${esc(f.executor_answer)}">${esc(ANSWER_LABELS[f.executor_answer])}</span>${f.executor_comment ? `<div class="nc-muted">${esc(f.executor_comment)}</div>` : ''}`
      : (canDo('normcheck.upload') ? `<button type="button" class="dt-btn nc-mini" data-nc-answer="${f.id}">Ответить</button>` : '—');
    const dec = f.decision
      ? `<span class="nc-dec nc-dec-${esc(f.decision)}">${esc(DECISION_LABELS[f.decision])}</span>${f.decision_reason ? `<div class="nc-muted">${esc(f.decision_reason)}</div>` : ''}`
      : (canDo('normcheck.decide') ? `<button type="button" class="dt-btn nc-mini" data-nc-decide="${f.id}">Решить</button>` : '—');
    const sel = canDo('normcheck.decide')
      ? `<td class="nc-find-sel"><input type="checkbox" data-nc-fsel="${f.id}"${this.selected.has(f.id) ? ' checked' : ''} /></td>` : '';
    return `<tr class="${f.hidden_by_b ? 'nc-row-hidden' : ''}">
      ${sel}
      <td>${sev}${cat}${this.findingMarks(f)}<div>${esc(f.issue)}</div>${f.fix ? `<div class="nc-muted">Как исправить: ${esc(f.fix)}</div>` : ''}${place ? `<div>${place}</div>` : ''}</td>
      <td>${norm}${normQuote}</td>
      <td>${ans}</td>
      <td>${dec}</td>
    </tr>`;
  }

  async onClick(e) {
    if (e.target.closest('[data-nc-analyze]')) { e.preventDefault(); await this.analyze(); return; }
    if (e.target.closest('[data-nc-refresh-progress]')) { e.preventDefault(); await this.renderProgress(); if (this.tab === 'findings') await this.reloadFindings(); return; }
    if (e.target.closest('[data-nc-add-finding]')) { e.preventDefault(); await this.addFinding(); return; }
    if (e.target.closest('[data-nc-rework]')) { e.preventDefault(); await this.rework(); return; }
    const bulk = e.target.closest('[data-nc-bulk-dec]');
    if (bulk) { e.preventDefault(); await this.bulkDecide(bulk.dataset.ncBulkDec); return; }
    const place = e.target.closest('[data-nc-find-page]');
    if (place) { e.preventDefault(); await this.openPageById(Number(place.dataset.ncFindPage)); return; }
    const ans = e.target.closest('[data-nc-answer]');
    if (ans) { e.preventDefault(); await this.answer(Number(ans.dataset.ncAnswer)); return; }
    const dec = e.target.closest('[data-nc-decide]');
    if (dec) { e.preventDefault(); await this.decide(Number(dec.dataset.ncDecide)); return; }
  }

  onChange(e) {
    const hid = e.target.closest('[data-nc-show-hidden]');
    if (hid) { this.showHidden = hid.checked; this.reloadFindings(); return; }
    const sel = e.target.closest('[data-nc-fsel]');
    if (sel) { const id = Number(sel.dataset.ncFsel); if (sel.checked) this.selected.add(id); else this.selected.delete(id); }
  }

  async analyze() {
    if (!confirm('Запустить ИИ-проверку раздела? Прежние ИИ-замечания этой проверки будут заменены.')) return;
    try {
      await api.normcheck.analyze(this.job.id);
      showNotification('Проверка запущена — ИИ разберёт раздел в фоне. Обновляйте ход проверки.', 'success');
      await this.renderProgress();
    } catch (e) { showNotification(apiErrorText(e), 'error'); }
  }

  async bulkDecide(decision) {
    const ids = [...this.selected];
    if (!ids.length) { showNotification('Отметьте замечания галочками', 'error'); return; }
    let done = 0;
    for (const id of ids) {
      try { await api.normcheck.patchFinding(id, { decision }); done += 1; } catch { /* пропускаем */ }
    }
    this.selected.clear();
    showNotification(`Решение «${DECISION_LABELS[decision]}» применено к ${done} из ${ids.length}`, 'success');
    await this.reloadFindings();
  }

  async openPageById(pageId) {
    try {
      const r = await api.normcheck.pages(this.job.id);
      const p = (r.items || []).find(x => x.id === pageId);
      if (p) await openPagePreview(p);
      else showNotification('Страница не найдена', 'error');
    } catch (e) { showNotification(apiErrorText(e), 'error'); }
  }

  reloadFindings() { return this.loadFindings(this.root.querySelector('[data-nc-panel]')); }

  async addFinding() {
    const saved = await openFindingForm();
    if (!saved) return;
    try {
      await api.normcheck.createFinding(this.job.id, saved);
      showNotification('Замечание добавлено', 'success');
      await this.reloadFindings();
    } catch (e) { showNotification(apiErrorText(e), 'error'); }
  }

  async answer(fid) {
    const res = await openAnswerForm();
    if (!res) return;
    try { await api.normcheck.patchFinding(fid, res); showNotification('Ответ сохранён', 'success'); await this.reloadFindings(); }
    catch (e) { showNotification(apiErrorText(e), 'error'); }
  }

  async decide(fid) {
    const res = await openDecisionForm();
    if (!res) return;
    try { await api.normcheck.patchFinding(fid, res); showNotification('Решение сохранено', 'success'); await this.reloadFindings(); }
    catch (e) { showNotification(apiErrorText(e), 'error'); }
  }

  rework() {
    // Форма загрузки исправленной версии на правой панели; onDone переоткрывает
    // ту же панель-синглтон карточкой новой версии.
    openUploadWizard({
      sheetId: this.job.project_sheet_id,
      sheetLabel: sheetTitle(this.job),
      prevJob: this.job,
      onDone: (job) => openCheckCard(job.id),
    });
  }
}

// ── Формы замечания / ответа / решения ────────────────────────────────────────

function openFindingForm() {
  return openDialog({
    title: 'Замечание нормоконтролёра',
    confirmLabel: 'Добавить',
    bodyHtml: `
      <div class="nc-form-2col">
        <label class="modal-field"><span class="modal-field-label">Важность</span>
          <select name="severity" class="modal-select">
            ${SEVERITIES.map(s => `<option value="${s}"${s === 'major' ? ' selected' : ''}>${esc(SEVERITY_LABELS[s])}</option>`).join('')}
          </select></label>
        <label class="modal-field"><span class="modal-field-label">Категория</span>
          <select name="category" class="modal-select">
            <option value="">—</option>
            ${CATEGORIES.map(c => `<option value="${c}">${esc(CATEGORY_LABELS[c])}</option>`).join('')}
          </select></label>
      </div>
      <label class="modal-field"><span class="modal-field-label">Суть замечания</span>
        <textarea name="issue" rows="2" class="modal-textarea" required></textarea></label>
      <div class="nc-form-2col">
        ${field('req_code', 'Шифр нормы', '', 'placeholder="СП 17.13330"')}
        ${field('req_clause', 'Пункт', '', 'placeholder="п. 5.1"')}
      </div>
      <label class="modal-field"><span class="modal-field-label">Цитата нормы (необязательно)</span>
        <textarea name="req_quote" rows="2" class="modal-textarea"></textarea></label>
      <label class="modal-field"><span class="modal-field-label">Как исправить</span>
        <textarea name="fix" rows="2" class="modal-textarea"></textarea></label>
      ${field('location_page', 'Страница (необязательно)', '', 'type="number" min="1"')}`,
    onConfirm: (dlg) => {
      const g = (n) => dlg.querySelector(`[name="${n}"]`);
      const issue = g('issue').value.trim();
      if (!issue) { showNotification('Укажите суть замечания', 'error'); return false; }
      return {
        severity: g('severity').value,
        category: g('category').value || null,
        issue,
        req_code: g('req_code').value.trim() || null,
        req_clause: g('req_clause').value.trim() || null,
        req_quote: g('req_quote').value.trim() || null,
        norm_status: g('req_code').value.trim() ? 'в_базе' : null,
        fix: g('fix').value.trim() || null,
        location_page: g('location_page').value ? Number(g('location_page').value) : null,
      };
    },
  });
}

function openAnswerForm() {
  return openDialog({
    title: 'Ответ исполнителя',
    confirmLabel: 'Сохранить',
    bodyHtml: `
      <label class="modal-field"><span class="modal-field-label">Ответ</span>
        <select name="executor_answer" class="modal-select">
          <option value="fixed">Исправлено</option>
          <option value="disagree">Не согласен</option>
        </select></label>
      <label class="modal-field"><span class="modal-field-label">Комментарий</span>
        <textarea name="executor_comment" rows="3" class="modal-textarea"></textarea></label>`,
    onConfirm: (dlg) => ({
      executor_answer: dlg.querySelector('[name="executor_answer"]').value,
      executor_comment: dlg.querySelector('[name="executor_comment"]').value.trim() || null,
    }),
  });
}

function openDecisionForm() {
  return openDialog({
    title: 'Решение нормоконтролёра',
    confirmLabel: 'Сохранить',
    bodyHtml: `
      <label class="modal-field"><span class="modal-field-label">Решение</span>
        <select name="decision" class="modal-select">
          <option value="accepted">Принято</option>
          <option value="dismissed">Снято</option>
          <option value="rework">На доработку</option>
        </select></label>
      <label class="modal-field"><span class="modal-field-label">Причина / комментарий</span>
        <textarea name="decision_reason" rows="3" class="modal-textarea"></textarea></label>`,
    onConfirm: (dlg) => ({
      decision: dlg.querySelector('[name="decision"]').value,
      decision_reason: dlg.querySelector('[name="decision_reason"]').value.trim() || null,
    }),
  });
}

// ── Вкладка «Страницы»: сетка превью с типом, фильтр, массовая смена (НК-3a2) ──

export const PAGE_KINDS = ['text', 'drawing', 'scan', 'blank'];
export const PAGE_KIND_LABELS = { text: 'Текст', drawing: 'Чертёж', scan: 'Скан', blank: 'Пустая' };

const effKind = (p) => p.kind_manual || p.kind;

export async function renderPagesPanel(host, job) {
  const canEdit = canDo('normcheck.upload');
  const wrap = document.createElement('div');
  wrap.className = 'nc-pages';
  host.innerHTML = '';
  host.appendChild(wrap);

  let pages = [];
  let status = job.pages_status;
  let filterKind = '';
  const selected = new Set();

  const load = async () => {
    wrap.innerHTML = '<div class="loading-spinner">Загрузка…</div>';
    try {
      const r = await api.normcheck.pages(job.id);
      pages = r.items; status = r.pages_status;
    } catch (e) { wrap.innerHTML = `<p class="cp-error">Ошибка: ${esc(apiErrorText(e))}</p>`; return; }
    render();
  };

  const render = () => {
    const summary = PAGE_KINDS
      .map(k => ({ k, n: pages.filter(p => effKind(p) === k).length }))
      .filter(x => x.n > 0)
      .map(x => `${esc(PAGE_KIND_LABELS[x.k].toLowerCase())}: ${x.n}`)
      .join(' · ') || 'страниц нет';
    const visible = pages.filter(p => !filterKind || effKind(p) === filterKind);
    const bulk = canEdit && selected.size
      ? `<div class="nc-bulk">Выбрано ${selected.size}: отметить как
           ${PAGE_KINDS.map(k => `<button type="button" class="dt-btn nc-mini" data-nc-bulk="${k}">${esc(PAGE_KIND_LABELS[k])}</button>`).join('')}
           <button type="button" class="dt-btn nc-mini" data-nc-bulk="auto">Авто</button></div>`
      : '';
    wrap.innerHTML = `
      <div class="nc-pages-toolbar">
        <span class="nc-pages-summary">${status === 'done' ? '' : esc(PAGES_STATUS_LABELS[status] || status) + ' · '}${summary}</span>
        <label class="nc-pages-filter">Тип:
          <select class="modal-select" data-nc-filter>
            <option value="">все</option>
            ${PAGE_KINDS.map(k => `<option value="${k}"${filterKind === k ? ' selected' : ''}>${esc(PAGE_KIND_LABELS[k])}</option>`).join('')}
          </select></label>
        ${status === 'marking' || status === 'queued' ? '<button type="button" class="dt-btn nc-mini" data-nc-refresh>Обновить</button>' : ''}
      </div>
      ${bulk}
      ${!pages.length
        ? `<p class="nc-muted">${
            status === 'done' ? 'Страниц нет (файл без страниц).'
              : status === 'failed' ? 'Разметка не удалась — попробуйте перезагрузить раздел.'
                : 'Разметка ещё идёт — нажмите «Обновить».'}</p>`
        : `<div class="nc-page-grid">${visible.map(p => tile(p)).join('')}</div>`}`;
  };

  const tile = (p) => {
    const kind = effKind(p);
    const thumb = p.has_preview
      ? `<img class="nc-page-img" loading="lazy" src="${api.normcheck.previewUrl(p.id, 'small')}" alt="стр. ${p.page_no}" />`
      : `<div class="nc-page-noimg">${kind === 'text' ? 'текст' : 'нет превью'}</div>`;
    const manual = p.kind_manual ? ' <span class="nc-page-manual" title="Тип задан вручную">✎</span>' : '';
    return `<figure class="nc-page nc-kind-${esc(kind)}" data-nc-page="${p.id}">
        ${canEdit ? `<input type="checkbox" class="nc-page-sel" data-nc-sel="${p.id}"${selected.has(p.id) ? ' checked' : ''} />` : ''}
        <button type="button" class="nc-page-thumb" data-nc-open="${p.id}" title="Открыть превью">${thumb}</button>
        <figcaption>
          <span class="nc-page-no">стр. ${p.page_no}</span>
          <span class="nc-kind-badge nc-kind-${esc(kind)}">${esc(PAGE_KIND_LABELS[kind])}</span>${manual}
          ${p.sheet_mark ? `<span class="nc-page-mark">${esc(p.sheet_mark)}</span>` : ''}
        </figcaption>
      </figure>`;
  };

  wrap.addEventListener('change', (e) => {
    const sel = e.target.closest('[data-nc-sel]');
    if (sel) { const id = Number(sel.dataset.ncSel); if (sel.checked) selected.add(id); else selected.delete(id); render(); return; }
    const flt = e.target.closest('[data-nc-filter]');
    if (flt) { filterKind = flt.value; render(); }
  });
  wrap.addEventListener('click', async (e) => {
    const open = e.target.closest('[data-nc-open]');
    if (open) {
      e.preventDefault();
      const p = pages.find(x => x.id === Number(open.dataset.ncOpen));
      const changed = await openPagePreview(p);
      if (changed) await load();
      return;
    }
    if (e.target.closest('[data-nc-refresh]')) { e.preventDefault(); await load(); return; }
    const bulk = e.target.closest('[data-nc-bulk]');
    if (bulk) {
      e.preventDefault();
      const kind = bulk.dataset.ncBulk === 'auto' ? null : bulk.dataset.ncBulk;
      try {
        await api.normcheck.bulkPages(job.id, { ids: [...selected], kind_manual: kind });
        selected.clear();
        showNotification('Тип страниц изменён', 'success');
        await load();
      } catch (err) { showNotification(apiErrorText(err), 'error'); }
    }
  });

  await load();
}

function openPagePreview(p) {
  if (!p) return;
  const canEdit = canDo('normcheck.upload');
  const img = p.has_preview
    ? `<img class="nc-preview-img" src="${api.normcheck.previewUrl(p.id, 'large')}" alt="стр. ${p.page_no}" />`
    : '<p class="nc-muted">Для этой страницы нет превью (например, файл DOCX или пустой лист).</p>';
  return openDialog({
    title: `Страница ${p.page_no}${p.sheet_mark ? ' · ' + p.sheet_mark : ''}`,
    wide: true,
    confirmLabel: 'Сохранить',
    bodyHtml: `
      <div class="nc-preview">${img}</div>
      <dl class="cp-reqs nc-preview-meta">
        <dt>Размер листа</dt><dd>${p.width_mm && p.height_mm ? `${p.width_mm}×${p.height_mm} мм (${p.orientation === 'landscape' ? 'альбомный' : 'книжный'})` : '—'}</dd>
        <dt>Основная надпись</dt><dd>${p.has_title_block ? 'есть (штамп)' : 'нет'}</dd>
      </dl>
      ${canEdit ? `
      <div class="nc-form-2col">
        <label class="modal-field"><span class="modal-field-label">Тип страницы</span>
          <select name="kind_manual" class="modal-select">
            <option value="">авто (${esc(PAGE_KIND_LABELS[p.kind])})</option>
            ${PAGE_KINDS.map(k => `<option value="${k}"${p.kind_manual === k ? ' selected' : ''}>${esc(PAGE_KIND_LABELS[k])}</option>`).join('')}
          </select></label>
        <label class="modal-field"><span class="modal-field-label">Марка / номер листа</span>
          <input name="sheet_mark" value="${esc(p.sheet_mark ?? '')}" placeholder="например, АР-1" /></label>
      </div>` : ''}`,
    onConfirm: canEdit ? async (dlg) => {
      const km = dlg.querySelector('[name="kind_manual"]').value || null;
      const sm = dlg.querySelector('[name="sheet_mark"]').value.trim();
      try {
        await api.normcheck.patchPage(p.id, { kind_manual: km, sheet_mark: sm });
        showNotification('Разметка сохранена', 'success');
        return true;
      } catch (e) { showNotification(apiErrorText(e), 'error'); return false; }
    } : null,
  });
}

// ── Паспорт проекта: таблица ключ — значение — ед. — источник — подтверждено ──

/** Пункт левого меню «Паспорт проекта» в карточке ИЛ (views/sheets.js). */
export function renderPassport(container, { sheetId }) {
  container.innerHTML = `
    <section class="dash-widget nc-passport-widget">
      <header class="dash-widget-header"><h3>Паспорт проекта</h3></header>
      <p class="ti-hint">Ключевые показатели проекта (ТЭП). Расхождения значений между источниками подсвечены. ИИ будет заполнять паспорт в НК-3b.</p>
      <div data-nc-passport></div>
    </section>`;
  renderPassportPanel(container.querySelector('[data-nc-passport]'), { sheetId });
}

export async function renderPassportPanel(host, { sheetId }) {
  const canEdit = canDo('normcheck.upload');
  const canDict = canDo('normcheck.decide');
  const wrap = document.createElement('div');
  wrap.className = 'nc-passport';
  host.innerHTML = '';
  host.appendChild(wrap);

  let data = null;
  let onlyDisc = false;  // фильтр «только расхождения»

  const load = async () => {
    wrap.innerHTML = '<div class="loading-spinner">Загрузка…</div>';
    try { data = await api.normcheck.facts(sheetId); }
    catch (e) { wrap.innerHTML = `<p class="cp-error">Ошибка: ${esc(apiErrorText(e))}</p>`; return; }
    render();
  };

  const srcCell = (v) => {
    const parts = [];
    if (v.source_part) parts.push(v.source_part === 'graphic' ? 'графика' : 'текст');
    if (v.source_page_no) parts.push('стр. ' + v.source_page_no);
    if (v.method === 'ai') parts.push('ИИ');
    else parts.push(esc(v.created_by || 'вручную'));
    return parts.join(', ');
  };

  const discBadge = (g) => {
    if (g.discrepancy) {
      const cls = g.critical ? 'nc-disc-badge nc-disc-critical' : 'nc-disc-badge';
      return ` <span class="${cls}" title="Расхождение значений${g.critical ? ' (критичный показатель)' : ''}">⚠ расхождение${g.critical ? ' (критично)' : ''}</span>`;
    }
    if (g.resolved) return ' <span class="nc-disc-ok" title="Источник подтверждён — расхождение снято">✓ сверено</span>';
    return '';
  };

  const valueRow = (g, v, span) => `
    <tr class="${g.discrepancy ? 'nc-fact-disc' : ''}">
      ${span ? `<td rowspan="${span}" class="nc-fact-name">${esc(g.name)}${discBadge(g)}</td>` : ''}
      <td class="nc-fact-val">${esc(v.value)}</td>
      <td>${esc(v.unit || g.unit || '')}</td>
      <td class="nc-muted">${srcCell(v)}</td>
      <td class="nc-fact-conf">${v.confirmed ? '✓' : '—'}</td>
      ${canEdit ? `<td class="nc-fact-actions">
        <button type="button" class="dt-btn nc-mini" data-nc-fact-edit="${v.id}">✎</button>
        <button type="button" class="dt-btn nc-mini" data-nc-fact-conf="${v.id}" data-conf="${v.confirmed ? 1 : 0}" title="${v.confirmed ? 'Снять отметку' : 'Это значение верное'}">${v.confirmed ? 'Снять ✓' : 'Верное'}</button>
        <button type="button" class="dt-btn nc-mini cp-danger" data-nc-fact-del="${v.id}">✕</button>
      </td>` : ''}
    </tr>`;

  const render = () => {
    const all = data.groups || [];
    const groups = onlyDisc ? all.filter(g => g.discrepancy) : all;
    const rows = groups.map(g => g.values.map((v, i) => valueRow(g, v, i === 0 ? g.values.length : 0)).join('')).join('');
    const discN = data.discrepancies || all.filter(g => g.discrepancy).length;
    wrap.innerHTML = `
      <div class="nc-section-head nc-passport-head">
        <span class="nc-muted">${all.length ? `показателей: ${all.length}` : ''}${discN ? ` · <span class="nc-crit">расхождений: ${discN}</span>` : ''}</span>
        <div class="nc-find-actions">
          <label class="nc-check-inline"><input type="checkbox" data-nc-only-disc${onlyDisc ? ' checked' : ''} /> только расхождения</label>
          ${canEdit ? '<button type="button" class="dt-btn" data-nc-reconcile title="Сверить значения между разделами и частями, завести замечания «несоответствие»">Сверить паспорт</button>' : ''}
          ${canDict ? '<button type="button" class="dt-btn" data-nc-key-new>Новый ключ словаря</button>' : ''}
          ${canEdit ? '<button type="button" class="dt-btn cp-primary" data-nc-fact-new>Добавить показатель</button>' : ''}
        </div>
      </div>
      ${!groups.length
        ? `<p class="nc-muted">${onlyDisc ? 'Расхождений нет.' : 'Показателей пока нет. Добавьте вручную или дождитесь ИИ-проверки.'}</p>`
        : `<table class="nc-fact-table"><thead><tr>
             <th>Показатель</th><th>Значение</th><th>Ед.</th><th>Источник</th><th>Подтв.</th>${canEdit ? '<th></th>' : ''}
           </tr></thead><tbody>${rows}</tbody></table>`}`;
  };

  wrap.addEventListener('change', (e) => {
    const t = e.target.closest('[data-nc-only-disc]');
    if (t) { onlyDisc = t.checked; render(); }
  });

  wrap.addEventListener('click', async (e) => {
    if (e.target.closest('[data-nc-reconcile]')) {
      e.preventDefault();
      try {
        const r = await api.normcheck.reconcile(sheetId);
        showNotification(`Сверка: расхождений ${r.discrepancies}, замечаний заведено/обновлено ${r.findings_created}, устранено ${r.findings_resolved}`, 'success');
        await load();
      } catch (err) { showNotification(apiErrorText(err), 'error'); }
      return;
    }
    if (e.target.closest('[data-nc-fact-new]')) {
      e.preventDefault();
      const saved = await openFactForm(data.keys);
      if (!saved) return;
      try { await api.normcheck.createFact(sheetId, saved); showNotification('Показатель добавлен', 'success'); await load(); }
      catch (err) { showNotification(apiErrorText(err), 'error'); }
      return;
    }
    if (e.target.closest('[data-nc-key-new]')) {
      e.preventDefault();
      const saved = await openFactKeyForm();
      if (!saved) return;
      try { await api.normcheck.createFactKey(saved); showNotification('Ключ добавлен в словарь', 'success'); await load(); }
      catch (err) { showNotification(apiErrorText(err), 'error'); }
      return;
    }
    const edit = e.target.closest('[data-nc-fact-edit]');
    if (edit) {
      e.preventDefault();
      const id = Number(edit.dataset.ncFactEdit);
      const v = (data.groups.flatMap(g => g.values)).find(x => x.id === id);
      const res = await openFactEditForm(v);
      if (!res) return;
      try { await api.normcheck.patchFact(id, res); showNotification('Сохранено', 'success'); await load(); }
      catch (err) { showNotification(apiErrorText(err), 'error'); }
      return;
    }
    const conf = e.target.closest('[data-nc-fact-conf]');
    if (conf) {
      e.preventDefault();
      try { await api.normcheck.patchFact(Number(conf.dataset.ncFactConf), { confirmed: conf.dataset.conf !== '1' }); await load(); }
      catch (err) { showNotification(apiErrorText(err), 'error'); }
      return;
    }
    const del = e.target.closest('[data-nc-fact-del]');
    if (del) {
      e.preventDefault();
      if (!confirm('Удалить значение показателя?')) return;
      try { await api.normcheck.deleteFact(Number(del.dataset.ncFactDel)); await load(); }
      catch (err) { showNotification(apiErrorText(err), 'error'); }
    }
  });

  await load();
}

function openFactForm(keys) {
  const opts = (keys || []).map(k => `<option value="${esc(k.key)}" data-unit="${esc(k.unit || '')}">${esc(k.name)}${k.unit ? ' (' + esc(k.unit) + ')' : ''}</option>`).join('');
  return openDialog({
    title: 'Показатель паспорта проекта',
    confirmLabel: 'Добавить',
    bodyHtml: `
      <label class="modal-field"><span class="modal-field-label">Показатель (из словаря ТЭП)</span>
        <select name="key" class="modal-select" required>${opts}</select></label>
      <div class="nc-form-2col">
        <label class="modal-field"><span class="modal-field-label">Значение</span>
          <input name="value" required placeholder="например, 12500,5" /></label>
        <label class="modal-field"><span class="modal-field-label">Единица</span>
          <input name="unit" placeholder="подставится из словаря" /></label>
      </div>
      <label class="modal-field"><span class="modal-field-label">Часть раздела (источник)</span>
        <select name="source_part" class="modal-select">
          <option value="">—</option>
          <option value="text">текстовая</option>
          <option value="graphic">графическая</option>
        </select></label>
      <label class="modal-field"><span class="modal-field-label">Примечание</span>
        <input name="note" /></label>`,
    onMount: (dlg) => {
      const sel = dlg.querySelector('[name="key"]');
      const unit = dlg.querySelector('[name="unit"]');
      const sync = () => { const o = sel.options[sel.selectedIndex]; if (o && !unit.value) unit.placeholder = o.dataset.unit || ''; };
      sel.addEventListener('change', sync); sync();
    },
    onConfirm: (dlg) => {
      const g = (n) => dlg.querySelector(`[name="${n}"]`);
      const key = g('key').value;
      const value = g('value').value.trim();
      if (!key || !value) { showNotification('Выберите показатель и укажите значение', 'error'); return false; }
      return {
        key, value,
        unit: g('unit').value.trim() || null,
        source_part: g('source_part').value || null,
        note: g('note').value.trim() || null,
      };
    },
  });
}

function openFactEditForm(v) {
  return openDialog({
    title: 'Правка значения',
    confirmLabel: 'Сохранить',
    bodyHtml: `
      <div class="nc-form-2col">
        <label class="modal-field"><span class="modal-field-label">Значение</span>
          <input name="value" value="${esc(v?.value ?? '')}" required /></label>
        <label class="modal-field"><span class="modal-field-label">Единица</span>
          <input name="unit" value="${esc(v?.unit ?? '')}" /></label>
      </div>
      <label class="modal-field"><span class="modal-field-label">Примечание</span>
        <input name="note" value="${esc(v?.note ?? '')}" /></label>
      <label class="modal-field modal-check"><input type="checkbox" name="confirmed"${v?.confirmed ? ' checked' : ''} /> <span>Подтверждено</span></label>`,
    onConfirm: (dlg) => {
      const g = (n) => dlg.querySelector(`[name="${n}"]`);
      const value = g('value').value.trim();
      if (!value) { showNotification('Укажите значение', 'error'); return false; }
      return {
        value,
        unit: g('unit').value.trim() || null,
        note: g('note').value.trim() || null,
        confirmed: g('confirmed').checked,
      };
    },
  });
}

function openFactKeyForm() {
  return openDialog({
    title: 'Новый ключ словаря ТЭП',
    confirmLabel: 'Добавить',
    bodyHtml: `
      <div class="nc-form-2col">
        ${field('key', 'Ключ (латиница/кириллица, без пробелов)', '', 'required placeholder="напр. площадь_кровли"')}
        ${field('name', 'Название', '', 'required placeholder="Площадь кровли"')}
      </div>
      <div class="nc-form-2col">
        ${field('unit', 'Единица', '', 'placeholder="м²"')}
        <label class="modal-field"><span class="modal-field-label">Тип</span>
          <select name="kind" class="modal-select">
            <option value="number">Число</option>
            <option value="text">Текст</option>
          </select></label>
      </div>
      ${field('tolerance', 'Допуск расхождения (доля, напр. 0.01 = 1%)', '0', 'type="number" step="0.001" min="0"')}
      <label class="modal-field modal-check"><input type="checkbox" name="critical" /> <span>Критичный (расхождение возвращает раздел — замечание важности «критично»)</span></label>`,
    onConfirm: (dlg) => {
      const g = (n) => dlg.querySelector(`[name="${n}"]`);
      const key = g('key').value.trim();
      const name = g('name').value.trim();
      if (!key || !name) { showNotification('Укажите ключ и название', 'error'); return false; }
      return {
        key, name,
        unit: g('unit').value.trim() || null,
        kind: g('kind').value,
        tolerance: Number(g('tolerance').value || 0),
        critical: g('critical').checked,
      };
    },
  });
}

// ── Вкладка «Замечания»: все замечания всех проверок плоским списком (НК-3b) ───

const VERIFIED_FILTER = [
  { value: '', label: 'все' },
  { value: 'yes', label: 'подтверждено' },
  { value: 'unverified', label: 'не подтверждено' },
  { value: 'na', label: 'графика/н.п.' },
];

/** Вкладка «Замечания» в «Нормоконтроле»: фильтры ИЛ/раздел/норма/исполнитель/
 * статус/важность/не подтверждено. Экспортируется и зовётся из normcontrol.js. */
export function renderRemarksTab(container) {
  const wrap = document.createElement('div');
  wrap.className = 'nc-remarks';
  container.innerHTML = '';
  container.appendChild(wrap);

  const state = { severity: '', status: '', verified: '', show_hidden: false, norm: '', executor: '', section_code: '' };

  const toolbar = () => `
    <div class="nc-remarks-toolbar">
      <input class="modal-input" data-nc-f="section_code" placeholder="Раздел (шифр)" value="${esc(state.section_code)}" />
      <input class="modal-input" data-nc-f="norm" placeholder="Норма (шифр)" value="${esc(state.norm)}" />
      <input class="modal-input" data-nc-f="executor" placeholder="Исполнитель" value="${esc(state.executor)}" />
      <select class="modal-select" data-nc-f="severity">
        <option value="">важность: все</option>
        ${SEVERITIES.map(s => `<option value="${s}"${state.severity === s ? ' selected' : ''}>${esc(SEVERITY_LABELS[s])}</option>`).join('')}
      </select>
      <select class="modal-select" data-nc-f="status">
        <option value="">статус: все</option>
        ${STATUSES.map(s => `<option value="${s}"${state.status === s ? ' selected' : ''}>${esc(statusLabel(s))}</option>`).join('')}
      </select>
      <select class="modal-select" data-nc-f="verified">
        ${VERIFIED_FILTER.map(v => `<option value="${v.value}"${state.verified === v.value ? ' selected' : ''}>цитата: ${esc(v.label)}</option>`).join('')}
      </select>
      <label class="nc-check-inline"><input type="checkbox" data-nc-f="show_hidden"${state.show_hidden ? ' checked' : ''} /> снятые верификатором</label>
    </div>`;

  const mark = (f) => {
    const m = [];
    if (f.model) m.push('<span class="nc-tag nc-tag-ai">ИИ</span>');
    if (f.verified_quote === false) m.push('<span class="nc-tag nc-tag-unverified">не подтв.</span>');
    if (f.stage_b_verdict === 'confirm' || f.stage_b_verdict === 'adjust') m.push('<span class="nc-tag nc-tag-opus">Opus</span>');
    if (f.hidden_by_b) m.push('<span class="nc-tag nc-tag-rejected">снято</span>');
    return m.join(' ');
  };

  const row = (f) => {
    const il = f.sheet_ip_no || f.sheet_short_name || `ИЛ #${f.project_sheet_id}`;
    const norm = [f.req_code, f.req_clause].filter(Boolean).join(' ') || (f.norm_status === 'нет_в_базе' ? 'нет в базе' : '—');
    const ans = f.executor_answer ? esc(ANSWER_LABELS[f.executor_answer]) : '—';
    const dec = f.decision ? `<span class="nc-dec nc-dec-${esc(f.decision)}">${esc(DECISION_LABELS[f.decision])}</span>` : '<span class="nc-muted">открыто</span>';
    return `<tr class="${f.hidden_by_b ? 'nc-row-hidden' : ''}" data-nc-open-job="${f.job_id}">
      <td>${clip(il)}<div class="nc-muted">${esc([f.section_code, kindLabel(f.doc_kind)].filter(Boolean).join(' · '))} · v${f.job_version || ''}</div></td>
      <td><span class="nc-sev nc-sev-${esc(f.severity)}">${esc(SEVERITY_LABELS[f.severity] || f.severity)}</span> ${mark(f)}<div>${esc(f.issue)}</div></td>
      <td>${esc(norm)}${f.req_quote ? `<div class="nc-muted nc-quote">«${esc(f.req_quote)}»</div>` : ''}</td>
      <td>${clip(f.executor_name)}</td>
      <td>${ans}</td>
      <td>${dec}</td>
    </tr>`;
  };

  const load = async () => {
    const body = wrap.querySelector('[data-nc-remarks-body]');
    body.innerHTML = '<div class="loading-spinner">Загрузка…</div>';
    const params = { limit: 300 };
    for (const [k, v] of Object.entries(state)) { if (v) params[k] = v === true ? 1 : v; }
    let res;
    try { res = await api.normcheck.allFindings(params); }
    catch (e) { body.innerHTML = `<p class="cp-error">${esc(apiErrorText(e))}</p>`; return; }
    if (!res.items.length) { body.innerHTML = '<p class="nc-muted">Замечаний по фильтру нет.</p>'; return; }
    body.innerHTML = `<table class="nc-find-table nc-remarks-table"><thead><tr>
        <th>ИЛ / раздел</th><th>Замечание</th><th>Норма</th><th>Исполнитель</th><th>Ответ</th><th>Решение</th>
      </tr></thead><tbody>${res.items.map(row).join('')}</tbody></table>
      <p class="nc-muted">Показано ${res.items.length} из ${res.total}.</p>`;
  };

  wrap.innerHTML = `${toolbar()}<div data-nc-remarks-body></div>`;
  let timer = null;
  wrap.addEventListener('input', (e) => {
    const f = e.target.closest('[data-nc-f]');
    if (!f || f.type === 'checkbox' || f.tagName === 'SELECT') return;
    state[f.dataset.ncF] = f.value.trim();
    clearTimeout(timer); timer = setTimeout(load, 350);
  });
  wrap.addEventListener('change', (e) => {
    const f = e.target.closest('[data-nc-f]');
    if (!f) return;
    state[f.dataset.ncF] = f.type === 'checkbox' ? f.checked : f.value;
    load();
  });
  wrap.addEventListener('click', (e) => {
    const tr = e.target.closest('[data-nc-open-job]');
    if (tr) { e.preventDefault(); openCheckCard(Number(tr.dataset.ncOpenJob)); }
  });
  load();
}

// ── Вкладка «Метрики» (НК-3c, normcheck.decide) ────────────────────────────────

const pct = (x) => (x == null ? '—' : `${Math.round(x * 100)}%`);
const num = (x) => (x == null ? '—' : String(x));

/** Вкладка «Метрики»: таблицы по версиям промпта и по разделам, суточный счётчик
 * LLM-вызовов, версии промпта (+ «Новая версия»), статистика исполнителей. */
export function renderMetricsTab(container) {
  const canEdit = canDo('normcheck.decide');
  const wrap = document.createElement('div');
  wrap.className = 'nc-metrics';
  container.innerHTML = '';
  container.appendChild(wrap);

  const verRow = (r) => `<tr>
    <td>${esc(r.prompt_version || '—')}</td><td>${esc(r.model || '—')}</td>
    <td class="num">${num(r.jobs)}</td><td class="num">${num(r.findings)}</td>
    <td class="num">${num(r.critical)}</td>
    <td class="num">${num(r.accepted)}</td><td class="num">${num(r.dismissed)}</td>
    <td class="num">${num(r.hidden_by_b)}</td><td class="num">${num(r.unverified)}</td>
    <td class="num">${pct(r.accepted_share)}</td>
    <td class="num">${num(r.llm_calls)}</td>
    <td class="num">${r.llm_calls_per_10p == null ? '—' : r.llm_calls_per_10p}</td>
    <td class="num">${r.avg_seconds == null ? '—' : r.avg_seconds + ' c'}</td>
  </tr>`;

  const secRow = (r) => `<tr>
    <td>${esc(r.section_code || '—')}</td>
    <td class="num">${num(r.jobs)}</td><td class="num">${num(r.findings)}</td>
    <td class="num">${num(r.critical)}</td><td class="num">${num(r.accepted)}</td>
    <td class="num">${num(r.dismissed)}</td><td class="num">${num(r.unverified)}</td>
    <td class="num">${pct(r.accepted_share)}</td>
  </tr>`;

  const promptRow = (p) => `<tr class="${p.active ? 'nc-prompt-active' : ''}">
    <td>${esc(p.stage)}</td><td class="num">v${p.version}</td>
    <td>${p.active ? '<span class="nc-tag nc-tag-opus">активна</span>' : ''}</td>
    <td class="nc-muted">${esc((p.note || '').slice(0, 60))}</td>
    <td>${esc((p.text || '').slice(0, 80))}…</td>
    ${canEdit ? `<td>${p.active ? '' : `<button type="button" class="dt-btn nc-mini" data-nc-prompt-act="${p.id}">Активировать</button>`}</td>` : ''}
  </tr>`;

  const render = (m, ex) => {
    wrap.innerHTML = `
      <section class="nc-metrics-block">
        <div class="nc-section-head"><h4>Суточный лимит LLM-вызовов</h4></div>
        <p class="ti-hint">Сегодня использовано <b>${m.llm_used_today}</b> из <b>${m.llm_limit}</b>${m.llm_used_today >= m.llm_limit ? ' <span class="nc-crit">— лимит достигнут</span>' : ''}.</p>
      </section>
      <section class="nc-metrics-block">
        <div class="nc-section-head"><h4>По версиям промпта и модели</h4></div>
        ${m.by_version.length ? `<div class="nc-table-scroll"><table class="nc-fact-table nc-metrics-table"><thead><tr>
          <th>Промпт</th><th>Модель</th><th>Проверок</th><th>Замечаний</th><th>Критич.</th>
          <th>Принято</th><th>Снято НК</th><th>Снято B</th><th>Не подтв.</th><th>Доля принятых</th>
          <th>LLM-вызовов</th><th>На 10 стр.</th><th>Ср. время</th>
        </tr></thead><tbody>${m.by_version.map(verRow).join('')}</tbody></table></div>`
          : '<p class="nc-muted">Пока нет проверок с ИИ-разбором.</p>'}
      </section>
      <section class="nc-metrics-block">
        <div class="nc-section-head"><h4>По разделам</h4></div>
        ${m.by_section.length ? `<div class="nc-table-scroll"><table class="nc-fact-table nc-metrics-table"><thead><tr>
          <th>Раздел</th><th>Проверок</th><th>Замечаний</th><th>Критич.</th><th>Принято</th>
          <th>Снято НК</th><th>Не подтв.</th><th>Доля принятых</th>
        </tr></thead><tbody>${m.by_section.map(secRow).join('')}</tbody></table></div>`
          : '<p class="nc-muted">Нет данных по разделам.</p>'}
      </section>
      <section class="nc-metrics-block">
        <div class="nc-section-head"><h4>Версии мастер-промпта</h4>
          ${canEdit ? '<button type="button" class="dt-btn cp-primary" data-nc-prompt-new>Новая версия промпта</button>' : ''}
        </div>
        <div class="nc-table-scroll"><table class="nc-fact-table"><thead><tr>
          <th>Стадия</th><th>Версия</th><th></th><th>Примечание</th><th>Текст</th>${canEdit ? '<th></th>' : ''}
        </tr></thead><tbody>${m.prompts.map(promptRow).join('')}</tbody></table></div>
      </section>
      <section class="nc-metrics-block">
        <div class="nc-section-head"><h4>Статистика исполнителей (задел НК-4)</h4>
          <span class="nc-find-actions">
            <label class="nc-check-inline">с <input type="date" data-nc-ex-from /></label>
            <label class="nc-check-inline">по <input type="date" data-nc-ex-to /></label>
            <button type="button" class="dt-btn nc-mini" data-nc-ex-apply>Показать</button>
          </span>
        </div>
        ${ex.rows.length ? `<div class="nc-table-scroll"><table class="nc-fact-table"><thead><tr>
          <th>Исполнитель</th><th>Проверок</th><th>Замечаний</th><th>Критич.</th>
          <th>Не исправлено, хотя заявлено</th><th>Принято</th><th>Доля принятых</th>
        </tr></thead><tbody>${ex.rows.map(r => `<tr>
          <td>${esc(r.executor)}</td><td class="num">${num(r.jobs)}</td><td class="num">${num(r.findings)}</td>
          <td class="num">${num(r.critical)}</td><td class="num">${num(r.not_fixed)}</td>
          <td class="num">${num(r.accepted)}</td><td class="num">${pct(r.accepted_share)}</td>
        </tr>`).join('')}</tbody></table></div>`
          : '<p class="nc-muted">Нет данных по исполнителям за период.</p>'}
      </section>`;
  };

  const load = async (exParams = {}) => {
    wrap.innerHTML = '<div class="loading-spinner">Загрузка…</div>';
    try {
      const [m, ex] = await Promise.all([
        api.normcheck.metrics(), api.normcheck.executorStats(exParams),
      ]);
      render(m, ex);
    } catch (e) { wrap.innerHTML = `<p class="cp-error">Ошибка: ${esc(apiErrorText(e))}</p>`; }
  };

  wrap.addEventListener('click', async (e) => {
    if (e.target.closest('[data-nc-prompt-new]')) {
      e.preventDefault();
      const body = await openPromptForm();
      if (!body) return;
      try { await api.normcheck.createPrompt(body); showNotification('Новая версия промпта создана', 'success'); await load(); }
      catch (err) { showNotification(apiErrorText(err), 'error'); }
      return;
    }
    const act = e.target.closest('[data-nc-prompt-act]');
    if (act) {
      e.preventDefault();
      try { await api.normcheck.patchPrompt(Number(act.dataset.ncPromptAct), { active: true }); showNotification('Версия активирована', 'success'); await load(); }
      catch (err) { showNotification(apiErrorText(err), 'error'); }
      return;
    }
    if (e.target.closest('[data-nc-ex-apply]')) {
      e.preventDefault();
      const from = wrap.querySelector('[data-nc-ex-from]')?.value || '';
      const to = wrap.querySelector('[data-nc-ex-to]')?.value || '';
      await load({ date_from: from, date_to: to });
    }
  });

  load();
}

async function openPromptForm() {
  // Подставим текст активной версии выбранной стадии как основу правки.
  let prompts = [];
  try { prompts = await api.normcheck.prompts(); } catch { /* пусто */ }
  const activeOf = (stage) => prompts.find(p => p.stage === stage && p.active) || prompts.find(p => p.stage === stage);
  const baseA = activeOf('A')?.text || '';
  const baseB = activeOf('B')?.text || '';
  return openDialog({
    title: 'Новая версия мастер-промпта',
    confirmLabel: 'Создать и активировать',
    wide: true,
    bodyHtml: `
      <label class="modal-field"><span class="modal-field-label">Стадия</span>
        <select name="stage" class="modal-select">
          <option value="A">A — поиск (Sonnet)</option>
          <option value="B">B — верификация (Opus)</option>
        </select></label>
      <label class="modal-field"><span class="modal-field-label">Текст промпта (системная часть)</span>
        <textarea name="text" rows="16" class="modal-textarea" required></textarea></label>
      <label class="modal-field"><span class="modal-field-label">Примечание (что изменили)</span>
        <input name="note" placeholder="напр. ужесточил критерий критичности ПБ" /></label>
      <label class="modal-field modal-check"><input type="checkbox" name="activate" checked /> <span>Сделать активной (без автоматического А/Б)</span></label>`,
    onMount: (dlg) => {
      const stage = dlg.querySelector('[name="stage"]');
      const ta = dlg.querySelector('[name="text"]');
      const sync = () => { if (!ta.dataset.touched) ta.value = stage.value === 'B' ? baseB : baseA; };
      ta.addEventListener('input', () => { ta.dataset.touched = '1'; });
      stage.addEventListener('change', () => { delete ta.dataset.touched; sync(); });
      sync();
    },
    onConfirm: (dlg) => {
      const g = (n) => dlg.querySelector(`[name="${n}"]`);
      const text = g('text').value.trim();
      if (!text) { showNotification('Укажите текст промпта', 'error'); return false; }
      return { stage: g('stage').value, text, note: g('note').value.trim() || null, activate: g('activate').checked };
    },
  });
}
